import type {ReactElement, RefObject} from 'react';
import {useEffect, useRef, useState} from 'react';
import {isIOS} from 'react-device-detect';
import {ClickableDiv} from '@Components/clickable-div';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {SimpleMediaCarousel} from '@Components/simple-media-carousel/simple-media-carousel';
import type {MediaItemData} from '@Components/simple-media-carousel/simple-media-carousel.types';
import type {GraphicMeta, GraphicsMetaMapType} from '@Components/social-media/publish-params.types';
import type {DesignSelection} from '@Components/social-media/social-media.types';
import {Text, TextSize} from '@Components/text';
import {pollForUpdatesInURL} from '@Libraries/poster-library';
import {seekAndPauseAutoplayVideo} from '@Libraries/social-media-grid.library';
import {noop} from '@Utils/general.util';
import {isVideoUrl} from '@Utils/media.util';
import {getCacheBustedURL} from '@Utils/url.util';
import {PreviewVideoPlayer} from '@Components/social-media/components/preview-video-player/preview-video-player';
import styles from './social-post-content-media-wrapper.module.scss';
import type {SocialPostContentMediaWrapperProps} from './social-post-content-media-wrapper.types';
import {
  FIRST_FRAME_SEEK_SECONDS,
  getFeedMediaLayout,
  MAX_SIDE_BY_SIDE_CELLS,
  MIN_SIDE_BY_SIDE_CELLS,
  PreviewMediaBackdrop,
  PreviewMediaFit,
  PreviewMediaLayout,
  SIDE_BY_SIDE_CELLS_PER_ROW,
} from './social-post-content-media-wrapper.types';
import {chunkIntoRows, getAssetIndexes, getOrderedCarouselMediaItems, getPreviewAssetElementId} from './social-post-content-media-wrapper.util';
import {useAssetRetryCacheBusting} from './social-post-content-media-wrapper.hooks';

export function SocialPostContentMediaWrapper({
  designs,
  designsGraphicsMetaMap,
  graphicsLoadingMap,
  doRetryOnError = false,
  onAssetErrored = noop,
  onAssetLoad = noop,
  onOrphanedPreviewContainerClick,
  layout,
  fit = PreviewMediaFit.CONTAIN,
  backdrop = PreviewMediaBackdrop.DARK,
  frameClassName,
  carouselClassName,
  carouselButtonContainerClassName,
  areCarouselArrowsAlwaysVisible,
  carouselButtonType,
  carouselIndicatorClassName,
  carouselCounterClassName,
  enableVideoAutoplay = false,
}: SocialPostContentMediaWrapperProps): ReactElement {
  const [designsGraphicsMetaMapState, setDesignsGraphicsMetaMapState] = useState<GraphicsMetaMapType>({});
  const videoRefsByAssetIndex = useRef<Record<number, RefObject<HTMLVideoElement | null>>>({});
  const {isCacheBustingNeeded, scheduleRetry, stopCacheBusting} = useAssetRetryCacheBusting();

  const haveAllPostersLoaded = (): boolean => {
    return Object.values(graphicsLoadingMap).every((pageLoadingMap) => {
      return Object.values(pageLoadingMap).every((isPageLoading) => isPageLoading);
    });
  };

  const isAnyPosterLoaded = (): boolean => {
    return Object.values(graphicsLoadingMap).some((pageLoadingMap) => {
      return Object.values(pageLoadingMap).some((isPageLoading) => isPageLoading);
    });
  };

  const shouldResetGraphicsMeta = (): boolean => {
    return Object.values(designsGraphicsMetaMap).some((pageGraphicsMetaMap) => {
      return Object.values(pageGraphicsMetaMap).some((graphicsMeta) => graphicsMeta.graphicURL === '');
    });
  };

  useEffect((): void => {
    if (haveAllPostersLoaded() || isAnyPosterLoaded()) {
      return;
    }

    if (shouldResetGraphicsMeta()) {
      setDesignsGraphicsMetaMapState({});
    } else {
      setDesignsGraphicsMetaMapState(designsGraphicsMetaMap);
    }
  }, [graphicsLoadingMap]);

  const getEffectiveLayout = (): PreviewMediaLayout => {
    return layout ?? getFeedMediaLayout(designs.length);
  };

  const getSideBySideRows = (): number[][] => {
    const visibleAssetCount = Math.max(Math.min(designs.length, MAX_SIDE_BY_SIDE_CELLS), MIN_SIDE_BY_SIDE_CELLS);

    return chunkIntoRows(getAssetIndexes(visibleAssetCount), SIDE_BY_SIDE_CELLS_PER_ROW);
  };

  const isStripLayout = (): boolean => {
    return getEffectiveLayout() === PreviewMediaLayout.STRIP;
  };

  const getAssetRows = (): number[][] => {
    if (getEffectiveLayout() === PreviewMediaLayout.SIDE_BY_SIDE) {
      return getSideBySideRows();
    }

    if (isStripLayout()) {
      return [getAssetIndexes(designs.length)];
    }

    return [[0]];
  };

  const isSingleAssetFrame = (): boolean => {
    const rows = getAssetRows();
    return rows.length === 1 && rows[0].length === 1;
  };

  const getNumberOfHiddenAssets = (): number => {
    if (isStripLayout()) {
      return 0;
    }

    return designs.length - MAX_SIDE_BY_SIDE_CELLS;
  };

  const isLastVisibleCell = (assetIndex: number): boolean => {
    return assetIndex === MAX_SIDE_BY_SIDE_CELLS - 1;
  };

  const getVideoRefForAsset = (assetIndex: number): RefObject<HTMLVideoElement | null> => {
    videoRefsByAssetIndex.current[assetIndex] ??= {current: null};
    return videoRefsByAssetIndex.current[assetIndex];
  };

  const getAssetClasses = (): string => {
    if (isStripLayout()) {
      return `${styles.mediaAsset} ${styles.mediaAssetStrip}`;
    }

    if (isSingleAssetFrame() && fit === PreviewMediaFit.CONTAIN) {
      return `${styles.mediaAsset} ${styles.mediaAssetContain}`;
    }

    return `${styles.mediaAsset} ${styles.mediaAssetCover}`;
  };

  const getGraphicURL = (url: string): string => {
    if (isCacheBustingNeeded) {
      return getCacheBustedURL(url);
    }

    return url;
  };

  const getCoverURL = (posterGraphicMeta: GraphicMeta): string | undefined => {
    return posterGraphicMeta.cover?.coverUrl;
  };

  const doRetryIfNeeded = (): void => {
    if (doRetryOnError) {
      scheduleRetry();
    }
  };

  const onLoad = ({posterHashedId, pageHashedId}: DesignSelection): void => {
    const urlToPoll = designsGraphicsMetaMapState[posterHashedId]?.[pageHashedId]?.graphicURL;

    if (!urlToPoll) {
      return;
    }

    pollForUpdatesInURL(urlToPoll, true)
      .promise.then((): void => {
        onAssetLoad({posterHashedId: posterHashedId, pageHashedId: pageHashedId});
        stopCacheBusting();
      })
      .catch((e: unknown) => {
        console.error(e);
      });
  };

  const getAssetErrorHandler = (design: DesignSelection): VoidFunction => {
    return (): void => {
      onAssetErrored(design);
      doRetryIfNeeded();
    };
  };

  const getAssetLoadHandler = (design: DesignSelection): VoidFunction => {
    return (): void => {
      onLoad(design);
    };
  };

  const getPlayBadgeIfNeeded = (): ReactElement | null => {
    if (enableVideoAutoplay) {
      return null;
    }

    return (
      <div className={styles.playBadgeHolder}>
        <Icon ariaHidden icon="icon-play-video" shape={IconShape.CIRCLE} size={IconSize.SIZE_ICON_16} type={IconType.SECONDARY} />
      </div>
    );
  };

  const getVideoPlayerIfNeeded = (assetIndex: number): ReactElement | null => {
    if (enableVideoAutoplay) {
      return null;
    }

    return <PreviewVideoPlayer videoRef={getVideoRefForAsset(assetIndex)} />;
  };

  const getImage = (assetIndex: number, design: DesignSelection, imageURL: string): ReactElement => {
    return (
      <img
        alt={window.i18next.t('pmwjs_social_media_graphic')}
        className={getAssetClasses()}
        id={getPreviewAssetElementId(assetIndex)}
        onError={getAssetErrorHandler(design)}
        onLoad={getAssetLoadHandler(design)}
        src={imageURL}
      />
    );
  };

  const getStillFrameForUnplayableVideo = (assetIndex: number, design: DesignSelection, stillFrameURL: string): ReactElement => {
    return (
      <>
        {getImage(assetIndex, design, stillFrameURL)}
        {getPlayBadgeIfNeeded()}
      </>
    );
  };

  const getVideoWithoutCoverForIOS = (assetIndex: number, design: DesignSelection, graphicURL: string): ReactElement => {
    return (
      <>
        <video
          autoPlay
          className={getAssetClasses()}
          id={getPreviewAssetElementId(assetIndex)}
          loop
          muted
          onError={getAssetErrorHandler(design)}
          onLoadedData={(e) => {
            if (!enableVideoAutoplay) {
              seekAndPauseAutoplayVideo(e, FIRST_FRAME_SEEK_SECONDS);
            }

            onLoad(design);
          }}
          playsInline
          ref={getVideoRefForAsset(assetIndex)}
          src={graphicURL}
        />
        {getVideoPlayerIfNeeded(assetIndex)}
      </>
    );
  };

  const getVideoMedia = (assetIndex: number, design: DesignSelection, graphicURL: string, coverUrl: string | undefined): ReactElement => {
    if (!isVideoUrl(graphicURL)) {
      return getStillFrameForUnplayableVideo(assetIndex, design, coverUrl ?? graphicURL);
    }

    if (isIOS && !coverUrl) {
      return getVideoWithoutCoverForIOS(assetIndex, design, graphicURL);
    }

    return (
      <>
        <video
          autoPlay={enableVideoAutoplay}
          className={getAssetClasses()}
          id={getPreviewAssetElementId(assetIndex)}
          loop
          muted
          onError={getAssetErrorHandler(design)}
          onLoadedMetadata={getAssetLoadHandler(design)}
          poster={coverUrl}
          ref={getVideoRefForAsset(assetIndex)}
          src={graphicURL}
        />
        {getVideoPlayerIfNeeded(assetIndex)}
      </>
    );
  };

  const getMedia = (assetIndex: number): ReactElement | null => {
    if (assetIndex >= designs.length) {
      return null;
    }

    const design = designs[assetIndex];
    const designGraphicMeta = designsGraphicsMetaMapState[design.posterHashedId]?.[design.pageHashedId];

    if (!designGraphicMeta?.graphicURL) {
      return null;
    }

    const graphicURL = getGraphicURL(designGraphicMeta.graphicURL);

    if (designGraphicMeta.isVideo) {
      return getVideoMedia(assetIndex, design, graphicURL, getCoverURL(designGraphicMeta));
    }

    return getImage(assetIndex, design, graphicURL);
  };

  const getHiddenAssetsOverlay = (assetIndex: number): ReactElement | null => {
    if (getNumberOfHiddenAssets() <= 0 || !isLastVisibleCell(assetIndex)) {
      return null;
    }

    return (
      <div className={styles.hiddenAssetsOverlay}>
        <Text bold className="ct-neutral-primary-on-dark" size={TextSize.LARGE} val={window.i18next.t('pmwjs_plus_count', {number: getNumberOfHiddenAssets()})} />
      </div>
    );
  };

  const getMediaCellClasses = (): string => {
    const backdropClass = backdrop === PreviewMediaBackdrop.LIGHT ? styles.mediaCellLight : '';

    return `${styles.mediaCell} ${backdropClass} ${isStripLayout() ? styles.mediaCellStrip : ''}`;
  };

  const getMediaCell = (assetIndex: number): ReactElement => {
    return (
      <div className={getMediaCellClasses()} key={`preview-media-cell-${assetIndex}`}>
        {getMedia(assetIndex)}
        {getHiddenAssetsOverlay(assetIndex)}
      </div>
    );
  };

  const getMediaGrid = (): ReactElement => {
    return (
      <div className={styles.mediaGrid}>
        {getAssetRows().map((assetIndexesInRow, rowIndex) => {
          return (
            <div className={`${styles.mediaGridRow} ${isStripLayout() ? styles.mediaGridRowStrip : ''}`} key={`preview-media-row-${rowIndex}`}>
              {assetIndexesInRow.map(getMediaCell)}
            </div>
          );
        })}
      </div>
    );
  };

  const getMediaItemsForCarousel = (): MediaItemData[] => {
    return getOrderedCarouselMediaItems(designsGraphicsMetaMapState).map((mediaItem) => {
      return {
        mediaUrl: getGraphicURL(mediaItem.mediaUrl),
        isVideo: mediaItem.isVideo,
        externalMediaDesign: mediaItem.externalMediaDesign,
      };
    });
  };

  const getCarousel = (): ReactElement => {
    return (
      <SimpleMediaCarousel
        buttonContainerClasses={carouselButtonContainerClassName}
        areArrowsAlwaysVisible={areCarouselArrowsAlwaysVisible}
        buttonType={carouselButtonType}
        containerClasses={carouselClassName}
        carouselIndicatorClasses={carouselIndicatorClassName}
        counterClasses={carouselCounterClassName}
        doRetryOnIndividualAssetError={doRetryIfNeeded}
        mediaItems={getMediaItemsForCarousel()}
        onIndividualAssetErrored={onAssetErrored}
        onIndividualImageLoad={onLoad}
        onIndividualVideoLoad={onLoad}
      />
    );
  };

  const getFrameContent = (): ReactElement => {
    if (getEffectiveLayout() === PreviewMediaLayout.CAROUSEL) {
      return getCarousel();
    }

    return getMediaGrid();
  };

  const getFrameClasses = (): string => {
    if (frameClassName) {
      return `${styles.mediaFrame} ${frameClassName}`;
    }

    return `${styles.mediaFrame} ${styles.mediaFrameDefaultSize}`;
  };

  if (onOrphanedPreviewContainerClick) {
    return (
      <ClickableDiv className={`${getFrameClasses()} _cursor-pointer`} onClick={onOrphanedPreviewContainerClick}>
        {getFrameContent()}
      </ClickableDiv>
    );
  }

  return <div className={getFrameClasses()}>{getFrameContent()}</div>;
}
